import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type MotionProps,
  type Transition,
  type Variants,
} from 'framer-motion';
import { useEffect, type PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';
import { XmarkIcon } from '@dynatrace/strato-icons';

import { useMicroguideContext } from './hooks/useMicroguideContext.js';
import * as styles from './Microguide.sty.js';
import { Button } from '../../buttons/button/Button.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { moveEasing } from '../../core/utils/framer-motion-easings.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Content } from '../shared-components/Content.js';
import { Title } from '../shared-components/Title.js';
import { Visual } from '../shared-components/Visual.js';

//#endregion Typings

const contentVariants: Variants = {
  enter: (isNext: boolean) => {
    return {
      x: isNext ? '100%' : '-100%',
      opacity: 0,
    };
  },
  center: {
    x: 0,
    opacity: 1,
    position: 'relative',
  },
  exit: (isNext: boolean) => {
    return {
      x: isNext ? '-100%' : '100%',
      opacity: 0,
      position: 'absolute',
      left: 0,
    };
  },
};

const fadeProps: MotionProps = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    position: 'relative',
  },
  exit: {
    opacity: 0,
    position: 'absolute',
    left: 0,
  },
};

/**
 * Container for a single step of the Microguide.
 * @public
 */
export const Step = ({ children }: PropsWithChildren) => {
  const {
    onClose,
    currentStep,
    totalSteps,
    isNext,
    setHasContent,
    setHasTitle,
    closeButtonRef,
    closedOnKeydown,
  } = useMicroguideContext();
  const intl = useIntl();

  const shouldReduceMotion = useReducedMotion();
  const transitionProp: Transition = {
    duration: shouldReduceMotion ? 0 : AnimationsJS.SlideIn.Duration.Base,
    ease: moveEasing,
  };

  const [slots] = useSlots(children, {
    title: Title,
    visual: Visual,
    content: Content,
  });

  useEffect(() => {
    setHasTitle(slots.title !== undefined);
    setHasContent(slots.content !== undefined);
  }, [setHasContent, setHasTitle, slots.content, slots.title]);

  return (
    <Flex
      as={motion.div}
      flexDirection="column"
      gap={8}
      className={styles.stepContainer}
    >
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={16}
        paddingTop={8}
        paddingLeft={16}
        paddingRight={8}
      >
        <Flex
          flexItem
          paddingY={6}
          flexGrow={1}
          id="strato-microguide-title"
          style={{ position: 'relative' }}
        >
          <AnimatePresence initial={false}>
            {slots.title && (
              <motion.div
                {...fadeProps}
                transition={transitionProp}
                key={`strato-microguide-title-${currentStep}`}
              >
                {slots.title}
              </motion.div>
            )}
          </AnimatePresence>
        </Flex>
        <Button
          onClick={() => onClose(currentStep, currentStep + 1 === totalSteps)}
          onKeyDown={() => (closedOnKeydown.current = true)}
          ref={closeButtonRef}
          aria-label={intl.formatMessage({
            defaultMessage: 'Close microguide',
            id: '0yl3EugW5oCHVbO1',
            description: 'Close the microguide.',
          })}
          data-dt-name="CloseX"
        >
          <Button.Prefix>
            <XmarkIcon />
          </Button.Prefix>
        </Button>
      </Flex>
      {slots.visual && (
        <div className={styles.visualWrapper}>
          <AnimatePresence initial={false}>
            <motion.div
              {...fadeProps}
              transition={transitionProp}
              key={`strato-microguide-visual-${currentStep}`}
            >
              {slots.visual}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
      {slots.content && (
        <div className={styles.contentWrapper} id="strato-microguide-content">
          <AnimatePresence initial={false} custom={isNext}>
            <Flex
              as={motion.div}
              variants={contentVariants}
              className={styles.animatedContent}
              custom={isNext}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transitionProp}
              key={`strato-microguide-content-${currentStep}`}
              flexDirection="column"
            >
              {slots.content}
            </Flex>
          </AnimatePresence>
        </div>
      )}
    </Flex>
  );
};

Step['displayName'] = 'Microguide.Step';
